import { create } from 'zustand';
import type { DigitalCodeDto, DigitalCodePoolDto, DigitalCodesAddedDto, DigitalFileDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useCatalogStore } from '@/stores/catalog.store';

/**
 * What a digital product delivers (ADR-077): a download's files, a code's
 * pools — one product's at a time, as its editor shows them.
 */

type CodeState = 'available' | 'delivered';

interface CodeList {
  variantId: string;
  state: CodeState;
  search: string;
  items: DigitalCodeDto[];
  nextCursor: string | null;
  total: number | null;
  loading: boolean;
}

interface DigitalState {
  productId: string | null;
  files: DigitalFileDto[];
  pools: DigitalCodePoolDto[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  /** The pool open in its sheet, searched and paged on the server. */
  codes: CodeList | null;

  load: (productId: string, kind: 'DOWNLOAD' | 'CODE') => Promise<void>;
  uploadFile: (file: File) => Promise<boolean>;
  renameFile: (id: string, name: string) => Promise<boolean>;
  removeFile: (id: string) => Promise<boolean>;
  addCodes: (variantId: string, text: string) => Promise<DigitalCodesAddedDto | null>;
  openCodes: (variantId: string, state: CodeState, search?: string) => Promise<void>;
  moreCodes: () => Promise<void>;
  removeCode: (id: string) => Promise<boolean>;
  closeCodes: () => void;
  clearError: () => void;
}

const PAGE_SIZE = 20;

/** The product's variants carry the stock the pool is; its editor reads them again. */
async function refreshProduct(productId: string | null): Promise<void> {
  if (productId) await useCatalogStore.getState().open(productId);
}

export const useDigitalStore = create<DigitalState>((set, get) => ({
  productId: null,
  files: [],
  pools: [],
  loading: false,
  saving: false,
  error: null,
  codes: null,

  async load(productId, kind) {
    set({ productId, loading: true, error: null, codes: null, ...(get().productId !== productId ? { files: [], pools: [] } : {}) });
    try {
      if (kind === 'DOWNLOAD') {
        const page = await withSession(() => adminApi.digital.files(productId));
        if (get().productId === productId) set({ files: page.items, loading: false });
      } else {
        const page = await withSession(() => adminApi.digital.pools(productId));
        if (get().productId === productId) set({ pools: page.items, loading: false });
      }
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load what this product delivers.') });
    }
  },

  async uploadFile(file) {
    const { productId } = get();
    if (!productId) return false;
    set({ saving: true, error: null });
    try {
      const added = await withSession(() => adminApi.digital.addFile(productId, file));
      set((state) => ({ saving: false, files: [...state.files, added] }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not upload the file.') });
      return false;
    }
  },

  async renameFile(id, name) {
    set({ saving: true, error: null });
    try {
      const renamed = await withSession(() => adminApi.digital.renameFile(id, name));
      set((state) => ({ saving: false, files: state.files.map((file) => (file.id === id ? renamed : file)) }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not rename the file.') });
      return false;
    }
  },

  async removeFile(id) {
    set({ saving: true, error: null });
    try {
      await withSession(() => adminApi.digital.removeFile(id));
      set((state) => ({ saving: false, files: state.files.filter((file) => file.id !== id) }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not delete the file.') });
      return false;
    }
  },

  async addCodes(variantId, text) {
    const { productId } = get();
    set({ saving: true, error: null });
    try {
      const result = await withSession(() => adminApi.digital.addCodes(variantId, text));
      set((state) => ({
        saving: false,
        pools: state.pools.map((pool) => (pool.variantId === variantId ? { ...pool, available: result.available } : pool)),
      }));
      await refreshProduct(productId);
      return result;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not add the codes.') });
      return null;
    }
  },

  async openCodes(variantId, state, search = '') {
    set({ codes: { variantId, state, search, items: [], nextCursor: null, total: null, loading: true }, error: null });
    try {
      const page = await withSession(() =>
        adminApi.digital.codes(variantId, { state, limit: PAGE_SIZE, ...(search.trim() ? { search: search.trim() } : {}) }),
      );
      const current = get().codes;
      // A newer search or tab answered first: this one is stale.
      if (!current || current.variantId !== variantId || current.state !== state || current.search !== search) return;
      set({ codes: { ...current, items: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, loading: false } });
    } catch (error) {
      set((current) => ({ codes: current.codes ? { ...current.codes, loading: false } : null, error: errorMessage(error, 'Could not load the codes.') }));
    }
  },

  async moreCodes() {
    const list = get().codes;
    if (!list?.nextCursor || list.loading) return;
    set({ codes: { ...list, loading: true } });
    try {
      const page = await withSession(() =>
        adminApi.digital.codes(list.variantId, {
          state: list.state,
          limit: PAGE_SIZE,
          cursor: list.nextCursor ?? undefined,
          ...(list.search.trim() ? { search: list.search.trim() } : {}),
        }),
      );
      set((state) => ({
        codes: state.codes ? { ...state.codes, items: [...state.codes.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false } : null,
      }));
    } catch (error) {
      set((state) => ({ codes: state.codes ? { ...state.codes, loading: false } : null, error: errorMessage(error, 'Could not load more codes.') }));
    }
  },

  async removeCode(id) {
    const { productId, codes } = get();
    set({ saving: true, error: null });
    try {
      await withSession(() => adminApi.digital.removeCode(id));
      set((state) => ({
        saving: false,
        codes: state.codes ? { ...state.codes, items: state.codes.items.filter((code) => code.id !== id), total: state.codes.total === null ? null : state.codes.total - 1 } : null,
        pools: state.pools.map((pool) => (pool.variantId === codes?.variantId ? { ...pool, available: pool.available - 1 } : pool)),
      }));
      await refreshProduct(productId);
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not delete the code.') });
      return false;
    }
  },

  closeCodes() {
    set({ codes: null });
  },

  clearError() {
    set({ error: null });
  },
}));
